import React from "react";
import styles from "./things-hub-search.module.scss";
import CreateTicketIcon from "../../../assets/svg/transfer-arrow-up-icon.svg";
import CreateTicketIconActive from "../../../assets/svg/transfer-arrow-up-icon-active.svg";
import SpinnerWithVariant from "@src/components/atoms/SpinnerWithVariant/spinner-with-variant";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { RootState } from "@src/reducers/store";
import { CustomerTicket } from "@src/types/support";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useSelector } from "react-redux";
import { useCreateSupportTicketStream } from "@src/cloud-connect/support/hooks/use-create-support-ticket-stream";
import { useI18N } from "@src/language/language-context";
import { MAX_TICKET_TITLE_LENGTH } from "@src/cloud-connect/support/constants/constants";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";
import { useSSEUnavailable } from "@src/cloud-connect/sse/use-sse-unavailable";

interface ThingsHubSearchProps {
  input: string;
  isDragMode: boolean;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
}

export const ThingsHubSearch = ({
  input,
  isDragMode,
  setInput,
  textareaRef,
}: ThingsHubSearchProps) => {
  const cssClassName = "things-hub-search";

  const { submitSupportTicket, isSubmitLoading } =
    useCreateSupportTicketStream();

  const userId = useSelector(
    (state: RootState) => state.yourSlice?.authContext?.userId,
  );

  const i18n = useI18N();
  const isSSEUnavailable = useSSEUnavailable("service-support");

  const texts = {
    thingsSearchPlaceholder: i18n(
      "modules.thingsHubSearch.thingsSearchPlaceholder",
    ),
    issueTitleTooLongError: i18n(
      "modules.addRequestTechnicalSupportDrawerModal.issueTitleTooLongError",
    ),
    activeSubmitButtonText: i18n(
      "modules.thingsHubSearch.activeSubmitButtonText",
    ),
    inactiveSubmitButtonText: i18n(
      "modules.thingsHubSearch.inactiveSubmitButtonText",
    ),
    deactivateDragModeText: i18n(
      "modules.thingsHubSearch.deactivateDragModeText",
    ),
    sseUnavailableTooltip: i18n(
      "modules.sse.reconnectingActionsUnavailableTooltip",
    ),
  };

  const hasText = input.trim() !== "";
  const isTooLong = input.length > MAX_TICKET_TITLE_LENGTH;

  const isSubmitDisabled =
    !hasText || isTooLong || isDragMode || isSubmitLoading || isSSEUnavailable;

  const ticketButtonIcon = isSubmitDisabled
    ? CreateTicketIcon
    : CreateTicketIconActive;

  let tooltipText = texts.activeSubmitButtonText;

  if (isSSEUnavailable) {
    tooltipText = texts.sseUnavailableTooltip;
  } else if (!hasText) {
    tooltipText = texts.inactiveSubmitButtonText;
  } else if (isDragMode) {
    tooltipText = texts.deactivateDragModeText;
  }

  const handleSubmitRequestTechnicalSupport = async (
    e: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
  ): Promise<void> => {
    e.preventDefault();

    if (isSSEUnavailable) return;

    if (isTooLong) {
      errorSnackbar(texts.issueTitleTooLongError);
      return;
    }

    if (!userId) {
      errorSnackbar("Unable to create support ticket.");
      return;
    }

    const payload: CustomerTicket = {
      type: "CUSTOMER_TICKET",
      raised_by: userId,
      priority: null,
      status: "new",
      title: input,
      description: input,
    };

    await submitSupportTicket({
      payload,
      contextQueryDatasource: null,
      contextQueryEvents: null,
      onTicketCreated: () => setInput(""),
    });
  };

  const handleClick = (
    event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
  ) => {
    if (isSubmitDisabled) return;
    handleSubmitRequestTechnicalSupport(event);
  };

  return (
    <div className={styles[`${cssClassName}`]}>
      <textarea
        ref={textareaRef}
        placeholder={texts.thingsSearchPlaceholder}
        className={styles[`${cssClassName}__textarea`]}
        value={input}
        maxLength={MAX_TICKET_TITLE_LENGTH}
        rows={1}
        onChange={(e) => {
          setInput(e.target.value);
          e.target.style.height = "auto";
          e.target.style.height = e.target.scrollHeight + "px";
        }}
        onKeyDown={(e) => {
          const isEnter = e.key === "Enter";
          if (!isEnter) return;

          e.preventDefault();

          if (!isSubmitDisabled && !e.shiftKey) {
            handleSubmitRequestTechnicalSupport(e);
          }
        }}
      />

      <div className={styles[`${cssClassName}__submit-button`]}>
        {!isSubmitLoading ? (
          <CustomTooltip content={tooltipText} position="left">
            <IconButton
              fun={(e) => handleClick(e)}
              icon={ticketButtonIcon}
              disabled={isSubmitDisabled}
            />
          </CustomTooltip>
        ) : (
          <div className={styles[`${cssClassName}__spinner-wrapper`]}>
            <SpinnerWithVariant variant="small" />
          </div>
        )}
      </div>
    </div>
  );
};
